import { useCallback, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useDropzone } from "react-dropzone";
import {
    AlertDialog,
    AlertDialogContent,
    Button,
    Card,
    Dialog,
    DialogContent,
} from "@jhool-io/fe-components";
import { AddTextEditor } from "../../../../components/TextEditor/AddTextEditor/AddTextEditor";
import {
    IInvoiceNoteDocument,
    IInvoiceNotesResponse,
} from "../../../../utils/types/billing";
import { useEditInvoiceNote } from "../../hooks/billing.mutations";
import {
    useAddInvoiceNoteDoc,
    useDeleteInvoiceNoteDoc,
} from "../../../../hooks/mutations/billing";
import useToast from "../../../../hooks/useToast";

import {
    checkIfRichTextHasTextContent,
    cn,
    displayNameInRightFormat,
    formatDate,
} from "../../../../utils/helpers";
import FolderOpenIcon from "../../../../components/Icons/FolderOpen";
import TimesIcon from "../../../../icons/Times";
import DeleteIcon from "../../../../icons/Delete";
import ViewInvoiceNoteDoc from "../ViewInvoiceNoteDoc/ViewInvoiceNoteDoc";

interface EditInvoiceNoteFormProps {
    invoiceNote: IInvoiceNotesResponse;
    onFormSubmit: () => void;
}

export default function EditInvoiceNoteForm({
    invoiceNote,
    onFormSubmit,
}: EditInvoiceNoteFormProps) {
    const [description, setDescription] = useState(
        () => invoiceNote?.description || ""
    );
    const [documentToDeleteId, setDocumentToDeleteId] = useState<string | null>(
        null
    );
    const [documentInView, setDocumentInView] =
        useState<IInvoiceNoteDocument | null>(null);
    const [selectedFiles, setSelectedFiles] = useState<File[] | null>(null);

    const formData = new FormData();

    const { toast } = useToast();
    const queryClient = useQueryClient();

    const addInvoiceNoteDoc = useAddInvoiceNoteDoc();

    const deleteInvoiceNoteDoc = useDeleteInvoiceNoteDoc();

    const editInvoiceNote = useEditInvoiceNote(
        invoiceNote.invoice_id,
        invoiceNote.invoice_note_id
    );

    const onDrop = useCallback(
        (acceptedFiles: File[]) => {
            if (selectedFiles && selectedFiles.length > 0) {
                setSelectedFiles([...acceptedFiles, ...selectedFiles]);
            } else setSelectedFiles([...acceptedFiles]);
        },
        [selectedFiles]
    );

    const { getRootProps, isDragActive } = useDropzone({
        onDrop,
        maxFiles: 4,
        accept: {
            "application/pdf": [".pdf"],
            "image/*": [".png", ".webp", ".jpeg", ".jpg", ".avif"],
        },
    });

    // Function for setting selected files
    const handleSetSelectedFiles = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (e.target.files) {
            if (selectedFiles && selectedFiles.length > 0) {
                setSelectedFiles([...e.target.files, ...selectedFiles]);
            } else setSelectedFiles([...e.target.files]);
        }
    };

    const handleRemoveSelectedFile = (filename: string) => {
        if (selectedFiles) {
            const filesArr = [...selectedFiles].filter(
                (file) => file.name !== filename
            );
            if (filesArr.length === 0) setSelectedFiles(null);
            else setSelectedFiles(filesArr);
        }
    };

    const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();

        if (!checkIfRichTextHasTextContent(description)) {
            toast({ mode: "error", message: "Description is required" });
            return;
        }

        editInvoiceNote.mutate(
            { description },
            {
                onSuccess: (res) => {
                    toast({
                        mode: "success",
                        message:
                            res.message || "Internal note updated successfully",
                    });

                    if (selectedFiles && selectedFiles.length > 0) {
                        for (let i = 0; i < [...selectedFiles].length; i += 1) {
                            formData.append("documents", [...selectedFiles][i]);
                        }
                        addInvoiceNoteDoc.mutate(
                            {
                                invoice_note_id: invoiceNote.invoice_note_id,
                                formData,
                                invoice_id: invoiceNote.invoice_id,
                            },
                            {
                                onSuccess: () => {
                                    queryClient.invalidateQueries({
                                        queryKey: [
                                            "invoice-notes",
                                            invoiceNote.invoice_id,
                                        ],
                                    });
                                    toast({
                                        mode: "success",
                                        message:
                                            res.message ||
                                            "Internal note document added successfully",
                                    });
                                    onFormSubmit();
                                    setDescription("");
                                },
                                onError: () => {
                                    toast({
                                        mode: "error",
                                        message:
                                            "Could not add document to internal note",
                                    });
                                    onFormSubmit();
                                    setDescription("");
                                    queryClient.invalidateQueries({
                                        queryKey: [
                                            "invoice-notes",
                                            invoiceNote.invoice_id,
                                        ],
                                    });
                                },
                            }
                        );
                    } else {
                        queryClient.invalidateQueries({
                            queryKey: ["invoice-notes", invoiceNote.invoice_id],
                        });
                        setDescription("");
                        onFormSubmit();
                    }
                },
                onError: (err) => {
                    toast({
                        mode: "error",
                        message:
                            err.response?.data.message ||
                            "Could not update internal note at this time",
                    });
                },
            }
        );
    };

    const handleDeleteInvoiceNoteDoc = () => {
        deleteInvoiceNoteDoc.mutate(
            {
                invoice_id: invoiceNote.invoice_id,
                invoice_note_id: invoiceNote.invoice_note_id,
                dataToSend: {
                    document_ids: [documentToDeleteId ?? ""],
                },
            },
            {
                onSuccess: (res) => {
                    toast({
                        mode: "success",
                        message: res.message ?? "Document deleted successfully",
                    });
                    queryClient.invalidateQueries({
                        queryKey: ["invoice-notes", invoiceNote.invoice_id],
                    });
                    setDocumentToDeleteId(null);
                },
                onError: (err) => {
                    toast({
                        mode: "error",
                        message:
                            err.response?.data.message ||
                            "Could not delete document",
                    });
                },
            }
        );
    };

    return (
        <>
            {documentInView ? (
                <Dialog open={Boolean(documentInView)}>
                    <DialogContent
                        title="View Internal note attachement"
                        handleCloseDialog={() => {
                            setDocumentInView(null);
                        }}
                        showFooter={false}
                    >
                        <ViewInvoiceNoteDoc
                            url={documentInView.url}
                            contentType={
                                documentInView?.metadata?.content_type ?? ""
                            }
                            title={documentInView.document_name ?? ""}
                        />
                    </DialogContent>
                </Dialog>
            ) : null}
            <AlertDialog open={Boolean(documentToDeleteId)}>
                <AlertDialogContent
                    onSaveClick={handleDeleteInvoiceNoteDoc}
                    saveText="Delete Internal Note Document"
                    onCancelClick={() => setDocumentToDeleteId(null)}
                    title="Delete Internal Note Document"
                    description="Are you sure you want to delete this internal note document?"
                    isSaveBtnDisabled={deleteInvoiceNoteDoc.isLoading}
                    isCancelBtnDisabled={deleteInvoiceNoteDoc.isLoading}
                    isDeleting
                />
            </AlertDialog>
            <form id="edit-invoice-note-form" onSubmit={handleSubmit}>
                <div className="fg">
                    <AddTextEditor
                        defaultValue={description}
                        title="Enter internal note description"
                        onEditorTextChange={setDescription}
                        isRequiredFieldProvided
                    />
                </div>
                <div
                    className={cn(
                        "flex w-full items-center gap-x-[--spacing-items]  border border-dashed border-[--color-text-link] rounded-lg px-[--padding-card-x] py-[--padding-card-y]",
                        {
                            "border-[2px] border-dashed border-stroke":
                                isDragActive,
                        }
                    )}
                    {...getRootProps()}
                >
                    <div className="p-16 bg-[rgba(204,250,233,0.50)] rounded-md shrink-0">
                        <FolderOpenIcon />
                    </div>
                    <div className="flex flex-col">
                        <div className="text-base font-medium">
                            <label
                                htmlFor="file-upload"
                                className="font-semibold text-textLink"
                            >
                                Click to upload{" "}
                                <input
                                    className="w-full absolute left-[-99999px]"
                                    id="file-upload"
                                    name="file-upload"
                                    type="file"
                                    onChange={handleSetSelectedFiles}
                                />
                            </label>
                            or drag your file here
                        </div>
                        <p className="text-caption font-normal">
                            Maximum file size of 10 MB
                        </p>
                    </div>
                </div>
                <div className="mt-[--spacing-form-items]">
                    {selectedFiles &&
                        selectedFiles.map((file, i) => (
                            <div
                                key={`${file.name} - ${i + 1}`}
                                className="flex items-center justify-between w-full [border:1px_solid_var(--color-stroke)] rounded-md px-[--padding-card-x] py-[--padding-card-y] my-[--spacing-items]"
                            >
                                <div className="text-caption text-xs text-textSec">
                                    {file.name}
                                </div>
                                <Button
                                    variant="can-hover"
                                    aria-label="delete icon"
                                    size="auto"
                                    onClick={() =>
                                        handleRemoveSelectedFile(file.name)
                                    }
                                >
                                    <TimesIcon />
                                </Button>
                            </div>
                        ))}
                </div>
                {invoiceNote.documents && invoiceNote.documents.length > 0 ? (
                    <div className="grid grid-cols-[repeat(auto-fit,minmax(262px,1fr))] gap-2">
                        {invoiceNote.documents.map((doc) => {
                            return (
                                <Card
                                    key={doc.invoice_note_document_id}
                                    classNames="flex gap-x-1 py-2 justify-between items-center"
                                >
                                    <span className="text-caption font-medium text-left">
                                        {`${displayNameInRightFormat(
                                            {
                                                firstName:
                                                    doc.uploaded_by.first_name,
                                                lastName:
                                                    doc.uploaded_by.last_name,
                                            },
                                            false
                                        )}, ${formatDate(doc.created_at)}`}
                                    </span>
                                    <div className="flex justify-between items-center font-normal text-caption">
                                        <Button
                                            variant="can-hover"
                                            size="auto"
                                            onClick={() => {
                                                setDocumentToDeleteId(
                                                    doc.invoice_note_document_id
                                                );
                                            }}
                                        >
                                            <DeleteIcon />
                                        </Button>
                                    </div>
                                </Card>
                            );
                        })}
                    </div>
                ) : null}
            </form>
        </>
    );
}
